import React, { useState } from 'react';
import { Volume2, RotateCcw } from 'lucide-react';

/**
 * VoiceSettingsPanel - Reusable component for advanced 11Labs voice customization
 *
 * Props:
 * - voiceSettings: { stability, similarityBoost, speakerBoost }
 * - onChange: callback(voiceSettings) when settings change
 * - onPreview: optional callback() to trigger voice preview
 */
const VoiceSettingsPanel = ({ voiceSettings = {}, onChange = () => {}, onPreview = () => {} }) => {
  const [expanded, setExpanded] = useState(false);

  const stability = voiceSettings.stability ?? 0.75;
  const similarityBoost = voiceSettings.similarityBoost ?? 0.9;
  const speakerBoost = voiceSettings.speakerBoost !== false;

  const handleStabilityChange = (value) => {
    onChange({
      ...voiceSettings,
      stability: parseFloat(value)
    });
  };

  const handleSimilarityBoostChange = (value) => {
    onChange({
      ...voiceSettings,
      similarityBoost: parseFloat(value)
    });
  };

  const handleSpeakerBoostChange = () => {
    onChange({
      ...voiceSettings,
      speakerBoost: !speakerBoost
    });
  };

  const handleReset = () => {
    onChange({
      stability: 0.75,
      similarityBoost: 0.9,
      speakerBoost: true
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-transparent">
      {/* Header - Collapsible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition"
      >
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-sm text-gray-700">
            Advanced Voice Settings {expanded ? '▼' : '▶'}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          Stability: {stability.toFixed(2)} | Similarity: {similarityBoost.toFixed(2)}
        </span>
      </button>

      {/* Collapsed state */}
      {!expanded && (
        <div className="text-xs text-gray-500 italic">
          Click to customize voice stability, similarity, and clarity
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="space-y-5 mt-4">
          {/* Stability Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Stability: {stability.toFixed(2)}
              </label>
              <span className="text-xs text-gray-500">
                {stability < 0.4 ? 'Very Variable' : stability < 0.7 ? 'Variable' : stability < 0.85 ? 'Balanced' : 'Very Consistent'}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={stability}
              onChange={(e) => handleStabilityChange(e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>More Variable</span>
              <span>More Consistent</span>
            </div>
            <p className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border border-gray-100">
              <strong>Lower values (0.0-0.4):</strong> More natural variation, voice changes more
              <br />
              <strong>Higher values (0.8-1.0):</strong> More robotic, consistent but less natural
            </p>
          </div>

          {/* Similarity Boost Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Similarity Boost: {similarityBoost.toFixed(2)}
              </label>
              <span className="text-xs text-gray-500">
                {similarityBoost < 0.4 ? 'Less Similar' : similarityBoost < 0.7 ? 'Moderate' : 'Very Similar'}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={similarityBoost}
              onChange={(e) => handleSimilarityBoostChange(e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Less Similar to Speaker</span>
              <span>More Similar to Speaker</span>
            </div>
            <p className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border border-gray-100">
              <strong>Lower values (0.0-0.4):</strong> Voice sounds less like the original speaker
              <br />
              <strong>Higher values (0.8-1.0):</strong> Voice closely matches the original speaker
            </p>
          </div>

          {/* Speaker Boost Toggle */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={speakerBoost}
                onChange={handleSpeakerBoostChange}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">
                Speaker Boost
                <span className={`ml-2 text-xs ${speakerBoost ? 'text-green-600' : 'text-gray-500'}`}>
                  {speakerBoost ? '(Enabled)' : '(Disabled)'}
                </span>
              </span>
            </label>
            <p className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border border-gray-100 ml-7">
              Enhances clarity and presence in the output. When enabled, the voice will be clearer and more pronounced.
              When disabled, the voice may sound quieter or less present.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={() => onPreview()}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500 text-white text-sm font-medium rounded hover:bg-purple-600 transition"
            >
              <Volume2 className="w-4 h-4" />
              Preview Voice
            </button>
            <button
              onClick={handleReset}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300 transition flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>

          {/* Info Box */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <strong>💡 Tip:</strong> These settings control how the 11Labs text-to-speech voice sounds.
            Experiment with different values to find the perfect voice for your use case.
            Changes take effect on the next call.
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceSettingsPanel;
